import { combineReducers, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import incentive from '../../pages/Rover/Form/Incentive';
import FirebaseOpportunityService from '../../service/Firebase/opportunity';
import type { IOpportunityState } from '../../service/OpportunityService';
import opportunityInfo from './BasicInfo';
import mileStoneSchedule from './MileStoneSchedule';
import preliminaryProgram from './PreliminaryProgram';
import projectSource from './ProjectSource';
import projectTeam from './ProjectTeam';
import projectUses from './ProjectUses';
import vendor from './Vendor';

export const submitOpportunity = createAsyncThunk('opportunity/submit', async (_, arg: any) => {
  const { opportunity } = arg.getState();
  const { opportunityInfo, preliminaryProgram, projectUses, projectSource, mileStoneSchedule, projectTeam, vendor } = opportunity;
  const srv = new FirebaseOpportunityService();

  const { id } = await srv.saveOpportunity({
    opportunityInfo,
    preliminaryProgram,
    projectUses,
    projectSource,
    mileStoneSchedule,
    projectTeam,
    vendor,
  });

  return { id };
});

export const fetchOpportunity = createAsyncThunk('opportunity/fetch', async (docId: string) => {
  const srv = new FirebaseOpportunityService();
  const opportunity = await srv.fetchOpportunity(docId);
  return { opportunity };
});

export const deleteOpportunity = createAsyncThunk('opportunity/delete', async (id: string) => {
  const srv = new FirebaseOpportunityService();
  await srv.deleteOpportunity(id);
});

export const listOpportunities = createAsyncThunk('opportunity/list', async () => {
  const srv = new FirebaseOpportunityService();
  const opportunities = await srv.listOpportunities();
  return opportunities;
});

const initialState = {
  createdID: '',
  opportunityInfo: {},
  preliminaryProgram: {},
  projectUses: {},
  projectSource: {},
  milestoneSchedule: {},
  projectTeam: {},
  vendor: {},
  incentives: {},
  submitting: false,
  submitSuccess: false,
  submitError: false,
  fetching: false,
  fetchSuccess: false,
  fetchError: false,
  deleting: false,
  deleteSuccess: false,
  deleteError: false,
};

const opportunityMainSlice = createSlice({
  name: 'opportunityMain',
  initialState: {
    ...initialState,
    opportunities: [] as IOpportunityState[],
  },
  reducers: {
    clear: (state) => ({
      ...state,
      ...{
        submitting: false,
        submitSuccess: false,
        submitError: false,
        fetching: false,
        fetchSuccess: false,
        fetchError: false,
        deleting: false,
        deleteSuccess: false,
        deleteError: false,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(submitOpportunity.pending, (state, _action) => {
      state.submitting = true;
    });
    builder.addCase(submitOpportunity.fulfilled, (state, { payload }) => {
      state.submitSuccess = true;
      state.submitting = false;
      state.createdID = payload.id;
    });
    builder.addCase(submitOpportunity.rejected, (state, { error }) => {
      console.log('submitOpportunity.rejected', error);
      state.submitError = true;
      state.submitting = false;
    });

    builder.addCase(fetchOpportunity.fulfilled, (state, { payload }) => {
      state.fetching = false;
      const { opportunity } = payload;
      state.opportunityInfo = opportunity.opportunityInfo;
      state.preliminaryProgram = opportunity.preliminaryProgram;
      state.projectUses = opportunity.projectUses;
      state.projectSource = opportunity.projectSource;
      state.milestoneSchedule = opportunity.milestoneSchedule;
      state.projectTeam = opportunity.projectTeam;
      state.vendor = opportunity.vendor;
      state.incentives = opportunity.incentives;
    });
    builder.addCase(fetchOpportunity.pending, (state, { payload }) => {
      state.fetching = true;
    });
    builder.addCase(fetchOpportunity.rejected, (state, { error }) => {
      console.log('fetchOpportunity.rejected', error);
      state.fetching = false;
      state.fetchError = true;
    });

    builder.addCase(listOpportunities.fulfilled, (state, { payload }) => {
      console.log(payload);
      state.fetching = false;
      state.opportunities = payload;
    });
    builder.addCase(listOpportunities.pending, (state, { payload }) => {
      state.fetching = true;
    });
    builder.addCase(listOpportunities.rejected, (state, { error }) => {
      console.log('listOpportunities.rejected', error);
      state.fetching = false;
      state.fetchError = true;
    });

    builder.addCase(deleteOpportunity.fulfilled, (state, { payload }) => {
      state.deleting = false;
      state.deleteSuccess = true;
    });
    builder.addCase(deleteOpportunity.pending, (state, { payload }) => {
      state.deleting = true;
    });
    builder.addCase(deleteOpportunity.rejected, (state, { error }) => {
      console.log('deleteOpportunity.rejected', error);
      state.deleting = false;
      state.deleteError = true;
    });
  },
});

export const { clear } = opportunityMainSlice.actions;

const opportunityReducer = combineReducers({
  opportunityInfo,
  preliminaryProgram,
  projectUses,
  projectSource,
  mileStoneSchedule,
  projectTeam,
  vendor,
  incentive,
  opportunityMain: opportunityMainSlice.reducer,
});

export default opportunityReducer;
